'use client';

import type { Dayjs } from 'dayjs';

import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { Iconify } from 'src/components/iconify';
import { formatOrganisationsform } from 'src/utils/format-organisation-type';

import { Tidslinje } from './tidslinje';
import { hälsning, sammanfattning } from './valkomstlage';

import type { Välkomstläge, Tidslinjepost } from './valkomstlage';

// ----------------------------------------------------------------------

/** En siffra ur föreningens vardag, som länk under remsan. Ersätter de gamla summeringspillren. */
export type Välkomstfakta = {
  etikett: string;
  href: string;
  ikon: string;
};

type Props = {
  läge: Välkomstläge;
  idag: Dayjs;
  förnamn?: string;
  /** Utelämnad i hubben, som visar alla organisationer på en gång. */
  organisation?: {
    organisationsNamn?: string;
    organisationsTyp?: string | null;
    verksamhetsinriktning?: string | null;
  };
  fakta?: Välkomstfakta[];
  /**
   * Hubben byter förening innan den navigerar — annars landar man på rätt sida i fel förening.
   * Utan den öppnas länken direkt.
   */
  onÖppna?: (post: Tidslinjepost) => void;
};

/**
 * Startsidans första yta. Den hälsar, säger vad som väntar och visar förfallodagarna de
 * närmaste två månaderna som en remsa.
 *
 * Möter man den första gången finns inget att räkna upp; då säger den vad sidan är till för och
 * pekar på kom igång-guiden i stället.
 */
export default function Välkomstbanner({ läge, idag, förnamn, organisation, fakta = [], onÖppna }: Props) {
  const router = useRouter();

  const öppna = useCallback(
    (post: Tidslinjepost) => {
      if (onÖppna) {
        onÖppna(post);
        return;
      }
      if (!post.länk) return;
      if (post.internLänk === false) {
        window.open(post.länk, '_blank', 'noopener');
        return;
      }
      router.push(post.länk);
    },
    [onÖppna, router]
  );

  const ärHubb = !organisation;
  const ursprung = organisation
    ? [
        organisation.organisationsNamn,
        organisation.organisationsTyp
          ? formatOrganisationsform(organisation.organisationsTyp, organisation.verksamhetsinriktning)
          : undefined,
      ]
        .filter(Boolean)
        .join(', ')
    : 'Alla organisationer';

  return (
    <Card
      component="section"
      aria-labelledby="valkomst-rubrik"
      sx={{ px: { xs: 2.5, md: 4 }, pt: { xs: 2.5, md: 3.5 }, pb: { xs: 2, md: 3 } }}
    >
      {läge.typ === 'laddar' ? (
        <Laddar />
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {ursprung}
          </Typography>

          <Typography id="valkomst-rubrik" component="h1" variant="h4" sx={{ mt: 0.5 }}>
            {hälsning(läge, förnamn)}
          </Typography>

          <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary', maxWidth: '62ch' }}>
            {sammanfattning(läge, idag)}
          </Typography>

          {läge.typ === 'förstaGången' && (
            <Button
              component={RouterLink}
              href={paths.dashboard.komIgång}
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="solar:check-circle-bold" />}
              sx={{ mt: 2.5 }}
            >
              Öppna kom igång-guiden
            </Button>
          )}

          {läge.typ === 'ingenOrganisation' && (
            <Button
              component={RouterLink}
              href={paths.dashboard.organisation.register}
              variant="contained"
              color="primary"
              startIcon={<Iconify icon="solar:add-circle-bold" />}
              sx={{ mt: 2.5 }}
            >
              Lägg till organisation
            </Button>
          )}

          {(läge.typ === 'väntar' || läge.typ === 'lugnt') && (
            <Tidslinje
              poster={läge.typ === 'väntar' ? läge.poster : []}
              idag={idag}
              onÖppna={öppna}
              visaOrganisation={ärHubb}
            />
          )}

          {fakta.length > 0 && läge.typ !== 'förstaGången' && <Faktarad fakta={fakta} />}
        </>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------

function Faktarad({ fakta }: { fakta: Välkomstfakta[] }) {
  return (
    <Stack
      component="ul"
      direction="row"
      sx={{
        m: 0,
        mt: 2.5,
        pt: 2,
        pl: 0,
        listStyle: 'none',
        flexWrap: 'wrap',
        columnGap: 3,
        rowGap: 1,
        borderTop: (theme) => `1px solid ${theme.vars.palette.divider}`,
      }}
    >
      {fakta.map((faktum) => (
        <Box component="li" key={faktum.etikett}>
          <Link
            component={RouterLink}
            href={faktum.href}
            variant="body2"
            underline="hover"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'text.primary' }}
          >
            <Iconify icon={faktum.ikon} width={18} sx={{ color: 'text.secondary' }} />
            {faktum.etikett}
          </Link>
        </Box>
      ))}
    </Stack>
  );
}

/** Samma höjd som den färdiga bannern, så att korten under inte hoppar när läget är klart. */
function Laddar() {
  return (
    <Box aria-busy aria-label="Laddar startsidan">
      <Skeleton variant="text" width={180} />
      <Skeleton variant="text" width={220} height={36} />
      <Skeleton variant="text" width="60%" />
      <Skeleton variant="rounded" height={64} sx={{ mt: 3, display: { xs: 'none', sm: 'block' } }} />
    </Box>
  );
}
